/**
 * Guarantee a promise settles.
 *
 * Wraps a promise so that it always resolves: with its own value on success, or with `fallback`
 * if it rejects or outlasts `ms`. Use it around any third-party request the render pipeline
 * awaits — a promise that never settles doesn't throw, so a try/catch around the await can't
 * save you and the UI is stuck in its loading state forever.
 *
 * @param {Promise} promise - The promise to guard
 * @param {Object} [options]
 * @param {number} [options.ms] - How long to wait before giving up
 * @param {*} [options.fallback] - What to resolve with on failure or timeout
 * @param {string} [options.label] - Name used in the console warning
 *
 * @returns {Promise} Always resolves, never rejects
 */
export default (promise, { ms = 10000, fallback = null, label = 'request' } = {}) => new Promise((resolve) => {
  let settled = false;
  let timer = null;

  const finish = (value, problem) => {
    if (settled) {
      return;
    }

    settled = true;
    window.clearTimeout(timer);

    if (problem) {
      console.warn(`[ASTR] ${label} ${problem}`);
    }

    resolve(value);
  };

  timer = window.setTimeout(() => finish(fallback, `timed out after ${ms}ms`), ms);

  Promise.resolve(promise).then(
    value => finish(value),
    error => finish(fallback, `failed: ${error}`)
  );
});
